/**
 * InspectionWorkflowChecklist - Step 1 of the inspection completion workflow.
 *
 * Mirrors WorkflowChecklist's outer chrome (progress header, scroll body,
 * sticky CTA) but swaps the binary checkbox for the three-tier rating
 * picker that's specific to inspections (utils/inspectionRating):
 *
 *   - Good            (green — no action required)
 *   - Needs Attention (amber — follow up; a note is optional)
 *   - Critical        (red — requires a non-empty note, escalates)
 *
 * Each row also surfaces the Required tag, optional details + additional
 * details paragraphs, the per-item camera CTA, and the captured photo
 * strip — same contract as the WO workflow checklist so the two surfaces
 * feel consistent in the field.
 *
 * The "Continue to Verification" CTA is gated on every required item
 * having a rating (mirrors WorkflowChecklist's allCompleted gate).
 *
 * The recommendations + follow-up textareas are intentionally NOT here —
 * they live on Step 3 (Sign-Off) per the workflow design so the
 * inspector commits remarks as the last thing before submitting.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { ChecklistItem, CapturedPhotoPhases } from '../../../services/checklistService';
import type { ChecklistItemRating } from '../../../services/checklistInstanceService';
import { Alert, PhotoThumbnail } from '../../atoms';
import { ChecklistItemPhotoActions } from '../../molecules/ChecklistItemPhotoActions';
import { RatingPicker } from '../../molecules/RatingPicker';
import { ChecklistAddItemRow } from '../../molecules/ChecklistAddItemRow';
import type { InspectionRating } from '../../../utils/inspectionRating';
import type { CapturePhase, SlotUploadStatuses } from '../../molecules/ChecklistItemPhotoActions';
import { formatAttribution } from '../../../utils/formatDate';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface InspectionWorkflowChecklistProps {
	items: ChecklistItem[];
	onRateItem: (
		itemId: string,
		rating: ChecklistItemRating | null,
		ratingNote: string | null,
	) => Promise<void>;
	/** True when every required item has a rating — gates the Continue CTA. */
	allItemsRated: boolean;
	/** Advance to the verify step. */
	onContinue: () => void;
	/** Inline error banner text. */
	error: string | null;
	/**
	 * Per-item camera handler. Phase is undefined for single-photo items
	 * and 'before' / 'after' for before_after items so the parent's
	 * modal can scope the upload accordingly.
	 */
	onCapturePhotoForItem?: (itemId: string, phase?: CapturePhase) => void;
	/** Per-item local flags for "captured this session". Drives the
	 * "Done" badge before the parent refetch lands. */
	capturedPhotosByItem?: Map<string, CapturedPhotoPhases>;
	/** Per-item background-upload state, keyed by item id. See the matching
	 *  prop on the work-order WorkflowChecklist. */
	uploadStatusByItem?: Map<string, SlotUploadStatuses>;
	/** Expand-thumbnail handler — opens the parent's PhotoLightbox. */
	onExpandPhoto?: (photoUrl: string, meta: { caption: string | null; area: string | null }) => void;
	/**
	 * Rename a checklist item in place. Optional — when omitted the pencil
	 * affordance is hidden and titles stay read-only.
	 */
	onRenameItem?: (itemId: string, title: string) => Promise<void>;
	/**
	 * Append an ad-hoc item to this inspection. Optional — when omitted the
	 * "Add item" row is hidden.
	 */
	onAddItem?: (title: string) => Promise<void>;
	/** Optional extra classes for the outer wrapper */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const InspectionWorkflowChecklist: React.FC<InspectionWorkflowChecklistProps> = ({
	items,
	onRateItem,
	allItemsRated,
	onContinue,
	error,
	onCapturePhotoForItem,
	capturedPhotosByItem,
	uploadStatusByItem,
	onExpandPhoto,
	onRenameItem,
	onAddItem,
	className,
}) => {
	const sortedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder);

	/* Progress + required hint mirror WorkflowChecklist's header so the
	   two surfaces share the exact same top strip. */
	const ratedCount = sortedItems.filter((it) => !!(it as any).rating).length;
	const totalCount = sortedItems.length;
	const progressPercent = totalCount > 0 ? Math.round((ratedCount / totalCount) * 100) : 0;
	const requiredItems = sortedItems.filter((it) => it.isRequired);
	const requiredRemaining = requiredItems.filter((it) => !(it as any).rating).length;

	return (
		<div className={`inspection-workflow-checklist flex flex-col h-full ${className ?? ''}`} data-ref="organism-inspection-workflow-checklist">
			{/* --- Progress header --- */}
			<div className="inspection-workflow-checklist__progress px-16px pt-16px pb-12px">
				<div className="flex items-center justify-between mb-8px">
					<span className="text-14px font-sans font-semibold text-brand-text">
						{ratedCount} of {totalCount} rated
					</span>
					<span className="text-14px font-sans font-medium text-brand-text-muted">
						{progressPercent}%
					</span>
				</div>
				<div className="w-full h-8px bg-gray-200 rounded-full overflow-hidden">
					<div className="h-full bg-brand-primary rounded-full transition-all duration-300 ease-out" style={{ width: `${progressPercent}%` }} />
				</div>

				{requiredItems.length > 0 && requiredRemaining > 0 && (
					<p className="text-12px font-sans text-brand-text-muted mt-4px">
						{requiredRemaining} required item{requiredRemaining === 1 ? '' : 's'} remaining
					</p>
				)}

				{error && (
					<Alert variant="error" size="sm" icon className="mt-8px">{error}</Alert>
				)}
			</div>

			{/* --- Scrollable item list --- */}
			<div className="flex-1 overflow-y-auto px-16px pb-16px">
				<ul className="inspection-workflow-checklist__list flex flex-col gap-6px">
					{sortedItems.map((item) => (
						<InspectionWorkflowChecklistRow
							key={item.id}
							item={item}
							onRateItem={onRateItem}
							onCapturePhotoForItem={onCapturePhotoForItem}
							capturedPhotosByItem={capturedPhotosByItem}
							uploadStatusByItem={uploadStatusByItem}
							onExpandPhoto={onExpandPhoto}
							onRenameItem={onRenameItem}
						/>
					))}
				</ul>

				{sortedItems.length === 0 && (
					<p className="text-center text-14px font-sans text-brand-text-muted py-32px">
						No checklist items have been assigned to this inspection.
					</p>
				)}

				{/* --- Add an item on the fly ---
				    Adds to THIS inspection's checklist only; the source template
				    is untouched. Ad-hoc items are optional (see the hook's
				    addItem) so adding one mid-visit never blocks the inspector
				    from continuing. */}
				{onAddItem && (
					<ChecklistAddItemRow
						block="inspection-workflow-checklist"
						placeholder="e.g. East stairwell"
						onAddItem={onAddItem}
					/>
				)}
			</div>

			{/* --- Sticky bottom CTA --- */}
			<div className="inspection-workflow-checklist__footer px-16px pb-16px pt-8px border-t border-gray-100">
				{allItemsRated ? (
					<button type="button" onClick={onContinue} className="w-full inline-flex items-center justify-center gap-8px px-20px py-14px text-16px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg min-h-48px transition-colors shadow-sm">
						Continue to Verification
						<svg className="w-18px h-18px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
						</svg>
					</button>
				) : (
					<p className="text-center text-14px font-sans text-brand-text-muted py-4px">
						{requiredRemaining > 0
							? `${requiredRemaining} required item${requiredRemaining === 1 ? '' : 's'} remaining`
							: `${totalCount - ratedCount} item${totalCount - ratedCount !== 1 ? 's' : ''} remaining`}
					</p>
				)}
			</div>
		</div>
	);
};

/* ============================================
 * ROW SUBCOMPONENT
 * ============================================ */

interface InspectionWorkflowChecklistRowProps {
	item: ChecklistItem;
	onRateItem: InspectionWorkflowChecklistProps['onRateItem'];
	onCapturePhotoForItem?: (itemId: string, phase?: CapturePhase) => void;
	capturedPhotosByItem?: Map<string, CapturedPhotoPhases>;
	/** Per-item background-upload state, keyed by item id. See the matching
	 *  prop on the work-order WorkflowChecklist. */
	uploadStatusByItem?: Map<string, SlotUploadStatuses>;
	onExpandPhoto?: (photoUrl: string, meta: { caption: string | null; area: string | null }) => void;
	onRenameItem?: (itemId: string, title: string) => Promise<void>;
}

/**
 * Per-row container. Owns its own dissatisfied-note draft state so each
 * row stays isolated (no shared map in a parent that could leak between
 * unmounts). Mirrors the inspection-tab implementation but without the
 * `disabled` prop — the workflow surface is always interactive.
 */
const InspectionWorkflowChecklistRow: React.FC<InspectionWorkflowChecklistRowProps> = ({
	item,
	onRateItem,
	onCapturePhotoForItem,
	capturedPhotosByItem,
	uploadStatusByItem,
	onExpandPhoto,
	onRenameItem,
}) => {
	const rating = ((item as any).rating ?? null) as ChecklistItemRating | null;

	/* Inline title editing. The template's wording often doesn't match what
	   the inspector is actually standing in front of ("Lobby" vs "East
	   Lobby"), so the title is editable during the visit. Edits apply to this
	   inspection's checklist only — the source template is untouched. */
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [titleDraft, setTitleDraft] = useState(item.title);
	const titleInputRef = useRef<HTMLInputElement>(null);

	/* Re-sync when the server sends a different title (e.g. after a refetch). */
	useEffect(() => {
		setTitleDraft(item.title);
	}, [item.title]);

	useEffect(() => {
		if (isEditingTitle) titleInputRef.current?.select();
	}, [isEditingTitle]);

	const commitTitle = async () => {
		const trimmed = titleDraft.trim();
		setIsEditingTitle(false);
		if (!trimmed || trimmed === item.title || !onRenameItem) {
			/* Nothing to save — restore whatever the server has. */
			setTitleDraft(item.title);
			return;
		}
		await onRenameItem(item.id, trimmed);
	};
	/* The reason for the current tier. The adapter folds the retired
	   dissatisfied_note into ratingNote, so historical rows show their text
	   without a fallback here. */
	const persistedNote = item.ratingNote ?? '';

	/* Rating interaction lives in the shared RatingPicker molecule, so the
	   field workflow and the detail tab behave identically. */
	const handleRate = useCallback(
		async (next: InspectionRating | null, note: string | null) => {
			await onRateItem(item.id, next, note);
		},
		[item.id, onRateItem],
	);

	const completedByName = item.completedByName ?? null;
	const completedAt = item.completedAt ?? null;
	const showAttribution = !!rating && !!completedByName;

	return (
		<li className="inspection-workflow-checklist__item">
			<div className={['w-full flex flex-col rounded-lg border transition-all duration-150 overflow-hidden', rating ? 'bg-brand-surface/50 border-brand-border' : 'bg-white border-gray-200'].join(' ')}>
				<div className="flex items-stretch gap-8px">
					<div className="flex-1 p-12px">
						<div className="flex items-start justify-between gap-8px">
							{isEditingTitle ? (
								/* text-16px, not 15px: anything under 16px makes iOS
								   Safari zoom the page in when the field is focused. */
								<input
									ref={titleInputRef}
									type="text"
									value={titleDraft}
									onChange={(e) => setTitleDraft(e.target.value)}
									onBlur={commitTitle}
									onKeyDown={(e) => {
										if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
										if (e.key === 'Escape') { setTitleDraft(item.title); setIsEditingTitle(false); }
									}}
									aria-label="Item name"
									className="flex-1 min-w-0 px-8px py-6px text-16px font-sans font-medium leading-snug text-brand-text rounded-md border border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-44px"
								/>
							) : (
								<span className="inspection-workflow-checklist__item-title text-15px font-sans font-medium leading-snug text-brand-text">
									{item.title}
								</span>
							)}
							<div className="flex-shrink-0 flex items-center gap-4px">
								{item.isRequired && (
									<span className="text-10px font-sans font-medium text-amber-700 bg-amber-100 px-6px py-2px rounded uppercase tracking-wide">
										Required
									</span>
								)}
								{/* Rename affordance — hidden while editing and when the
								    parent didn't supply a rename handler. */}
								{onRenameItem && !isEditingTitle && (
									<button
										type="button"
										onClick={() => setIsEditingTitle(true)}
										aria-label={`Rename ${item.title}`}
										className="inspection-workflow-checklist__rename inline-flex items-center justify-center min-w-44px min-h-44px -my-8px text-brand-text-subtle hover:text-brand-primary transition-colors"
									>
										<svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" aria-hidden="true">
											<path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
										</svg>
									</button>
								)}
							</div>
						</div>

						{item.details && (
							<p className="text-13px font-sans leading-snug mt-4px whitespace-pre-line text-brand-text-muted">
								{item.details}
							</p>
						)}

						{item.additionalDetails && (
							<p className="text-12px font-sans leading-snug mt-4px italic px-8px py-4px rounded bg-amber-50 text-amber-800 border border-amber-100">
								{item.additionalDetails}
							</p>
						)}

						{/* Three-tier rating — Good / Needs Attention / Critical */}
						<RatingPicker
							className="mt-10px"
							rating={rating}
							note={persistedNote}
							onRate={handleRate}
						/>

					{showAttribution && (
							<span className="text-11px font-sans text-brand-text-muted mt-8px block">
								{completedByName} &middot;{' '}
								{completedAt
									? formatAttribution(completedAt)
									: 'just now'}
							</span>
						)}
					</div>

					{/* Camera CTA — shared molecule branches on item.photoMode. */}
					{onCapturePhotoForItem && (
						<ChecklistItemPhotoActions
							item={item}
							onCapture={onCapturePhotoForItem}
							captured={capturedPhotosByItem?.get(item.id)}
							uploadStatus={uploadStatusByItem?.get(item.id)}
							/* Every inspection item gets a camera, not only the
							   ones the template asked for a photo on. */
							optionalPhoto
						/>
					)}
				</div>

				{/* Captured photos strip — same layout as WO workflow,
				 *  with a BEFORE/AFTER overlay tag for before_after rows. */}
				{item.photos && item.photos.length > 0 && (
					<div className="flex flex-wrap gap-6px px-12px py-8px border-t border-gray-100 bg-gray-50/50">
						{item.photos.map((photo) => (
							<div key={photo.id} className="relative">
								<PhotoThumbnail src={photo.url} alt={photo.caption ?? 'Captured photo'} size="md" onExpand={onExpandPhoto ? () => onExpandPhoto(photo.url, { caption: photo.caption, area: photo.area }) : undefined} />
								{item.photoMode === 'before_after' && photo.photoPhase && (
									<span className="absolute top-2px left-2px text-9px font-bold uppercase tracking-wide bg-black/70 text-white px-4px py-1px rounded">
										{photo.photoPhase}
									</span>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</li>
	);
};

export default InspectionWorkflowChecklist;
